import { useMemo } from 'react';
import { spring, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { lalezarFontFamily } from '@/styles/fonts';

// Types
import type { TrainingTextProps } from '../../types';

// Constants
import {
  ELEMENT_DELAYS,
  LAYOUT_POSITIONS,
  ROTATION_ANGLE,
  CHAR_REVEAL_ANIMATION,
} from '../../constants';
import { CLAMP_BOTH } from '@/constants';

// Components
import { MaskRevealText } from '../MaskRevealText';

export const TrainingText = ({
  barScaleX,
  barScaleY,
  parallaxMid,
  exitOpacity = 1,
}: TrainingTextProps) => {
  const { top, left, fontSize, lineHeight } = LAYOUT_POSITIONS.trainingText;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Split text into individual characters for staggered reveal
  const characters = useMemo(() => CHAR_REVEAL_ANIMATION.text.split(''), []);

  // Fade bar in as scaleX grows from 0 to 0.3
  const barOpacity = interpolate(barScaleX, [0, 0.3], [0, 1], CLAMP_BOTH);

  return (
    <p
      className="absolute m-0 flex flex-col"
      style={{
        top,
        left,
        fontSize,
        lineHeight: `${lineHeight}px`,
        fontFamily: lalezarFontFamily,
        transform: `rotate(${ROTATION_ANGLE}) translate(${parallaxMid}px, ${parallaxMid}px)`, // Rotate and apply parallax offset
        opacity: exitOpacity,
      }}
    >
      <MaskRevealText animationDelay={ELEMENT_DELAYS.ofTraining}>
        <span className="text-white">of training</span>
      </MaskRevealText>
      <span
        className="inline-block overflow-hidden bg-secondary-250 px-5 text-primary-800"
        style={{
          transformOrigin: 'left center',
          transform: `scaleX(${barScaleX}) scaleY(${barScaleY})`, // Expand bar from left
          opacity: barOpacity, // Fade in while expanding
        }}
      >
        <span className="inline-block whitespace-nowrap">
          {characters.map((char, index) => {
            // Stagger each character's start frame by index
            const charStartFrame =
              ELEMENT_DELAYS.throughout +
              CHAR_REVEAL_ANIMATION.charStartOffset +
              index * CHAR_REVEAL_ANIMATION.charStaggerFrames;

            // Spring progress from 0 to 1 for this character
            const charProgress = spring({
              frame: frame - charStartFrame,
              fps,
              config: CHAR_REVEAL_ANIMATION.springConfig,
            });

            // Fade character in
            const charOpacity = interpolate(
              charProgress,
              [0, 1],
              [0, 1],
              CLAMP_BOTH,
            );

            // Slide character upward into position
            const translateY = interpolate(
              charProgress,
              [0, 1],
              [30, 0],
              CLAMP_BOTH,
            );

            // Scale character from 0.8 to full size
            const scale = interpolate(
              charProgress,
              [0, 1],
              [0.8, 1],
              CLAMP_BOTH,
            );

            return (
              <span
                key={`${char}-${index}`}
                style={{
                  display: 'inline-block',
                  opacity: charOpacity, // Spring-driven fade in
                  transform: `translateY(${translateY}px) scale(${scale})`, // Combined slide up and scale entrance
                }}
              >
                {char}
              </span>
            );
          })}
        </span>
      </span>
      <MaskRevealText animationDelay={ELEMENT_DELAYS.theYear}>
        <span className="text-white">the year</span>
      </MaskRevealText>
    </p>
  );
};
